'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { analytics } from '@/lib/analytics';
import { TrackingEvent } from '@/utils/helpers';

/**
 * Args passed to the render-function form of `primaryAction`. Lets the
 * caller wire loading state + a "close the modal" handler into the
 * primary button without lifting state into every parent component.
 *
 * Example:
 *
 *   primaryAction={({ close, loading, setLoading }) => (
 *     <Button
 *       disabled={loading}
 *       loading={loading}
 *       onClick={async () => {
 *         setLoading(true);
 *         try { await doThing(); close(); }
 *         finally { setLoading(false); }
 *       }}
 *     >Confirm</Button>
 *   )}
 */
export interface PrimaryActionRenderArgs {
  close: () => void;
  loading: boolean;
  setLoading: (next: boolean) => void;
}

interface Props {
  trigger: ReactNode;
  message: string | ReactNode;
  title: string;
  /**
   * Either a plain `ReactNode` (legacy — modal stays open after click)
   * or a render function that receives `{ close, loading, setLoading }`
   * for the modern "dismiss on success + show loading state" pattern.
   */
  primaryAction: ReactNode | ((args: PrimaryActionRenderArgs) => ReactNode);
  cancelText?: string;
  disabled?: boolean;
  isCancelable?: boolean;
  tracking?: TrackingEvent;
}

export const ActionModal = ({
  trigger,
  message,
  title,
  primaryAction,
  cancelText = 'Cancel',
  disabled = false,
  isCancelable = true,
  tracking,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleModal = () => {
    // Don't allow closing (outside-click, X button, Cancel) while the
    // primary action is in flight — easy to accidentally lose UI state.
    if (actionLoading) return;
    setIsOpen(!isOpen);
    if (tracking && isOpen) {
      analytics.capture(tracking.event, tracking.properties);
    }
  };

  const close = () => {
    setIsOpen(false);
    setActionLoading(false);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, actionLoading]);

  return (
    <>
      <span
        aria-disabled={disabled}
        onClick={disabled ? undefined : toggleModal}
        className={`text-zinc-500 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:text-white'}`}
      >
        {trigger}
      </span>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
          >
            <span className="flex justify-between items-center p-4 border-zinc-700">
              <span className="text-xl font-semibold">{title}</span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="p-4 flex flex-col gap-6">
              {typeof message === 'string' ? (
                <span className="text-zinc-400 text-left">{message}</span>
              ) : (
                message
              )}
              <span className={'flex flex-row gap-4'}>
                {isCancelable && (
                  <Button
                    className={'w-full'}
                    onClick={toggleModal}
                    isSecondary={true}
                    variant={'slim'}
                    disabled={actionLoading}
                  >
                    {cancelText}
                  </Button>
                )}
                {typeof primaryAction === 'function'
                  ? primaryAction({
                      close,
                      loading: actionLoading,
                      setLoading: setActionLoading,
                    })
                  : primaryAction}
              </span>
            </span>
          </span>
        </span>
      )}
    </>
  );
};
